/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './style.css';
import {
  AddIcon,
  CrossIcon,
  LeftArrowIcon,
  RefreshIcon,
  RightArrowIcon
} from 'assets/images';
import { useDispatch } from 'react-redux';
import { updateSleepSettings } from 'store/modules/settings';
import { hideDialog } from 'store/modules/base/dialog';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import join from 'lodash/join';
import utils from 'util/utils';
import withBoundary from 'component/ErrorBoundary/withBoundary';

const Browser = ({
  options,
  tabs,
  activeTab,
  style = {},
  theme = Light,
  onAddTabPress,
  onLeave
}) => {
  const [currTabs, setNewTabs] = useState(tabs);
  const [timer, setTimer] = useState(10);
  const [showTimer, setShowTimer] = useState(false);
  const {
    tabStyle = {},
    tabsStyle = {},
    contentStyle = {},
    activeTabsStyle = {},
    activeTabBoxStyle = {},
    tabBoxStyle = {},
    labelStyle = {}
  } = style;
  const [activeTabIndex, setActiveTabIndex] = useState(activeTab);
  const styles = themedStyle(theme);
  const dispatch = useDispatch();
  const intervalRef = useRef(null);
  useEffect(() => {
    if (options?.sessionExpiryLimit) {
      setShowTimer(true);
      if (intervalRef.current) clearInterval(intervalRef.current);
      setTimer((options?.sessionExpiryTime ?? 10) * 60);
      intervalRef.current = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () =>
      intervalRef.current ? clearInterval(intervalRef.current) : null;
  }, []);
  useEffect(() => {
    if (timer < 1) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
      dispatch(
        updateSleepSettings({
          userBrowserMode: false
        })
      );
      dispatch(hideDialog('Browser'));
    }
  }, [timer]);
  useEffect(() => setNewTabs(tabs), [tabs]);
  return (
    <div className="browser-tabs">
      <div className="tabs" style={{ ...styles.tabs, tabsStyle }}>
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          {currTabs && Array.isArray(currTabs)
            ? tabsLoop(
                options,
                currTabs,
                activeTabIndex,
                tabStyle,
                activeTabsStyle,
                tabBoxStyle,
                activeTabBoxStyle,
                setActiveTabIndex,
                setNewTabs,
                onAddTabPress,
                theme
              )
            : console.log('Some thing wrong with currtabs ', { currTabs })}
        </div>
        {showTimer && (
          <Box className="timerContainer">
            <Box className="timerLabel">
              {`time remaining ${utils.formatNumbers(
                utils.getHours(timer)
              )}:${utils.formatNumbers(utils.getMinutes(timer))}`}
            </Box>
          </Box>
        )}
        <Button style={styles.leaveButton} onClick={onLeave}>
          leave browser
        </Button>
      </div>
      {currTabs.map((item, index) => (
        <IframeTab
          options={options}
          activeTabIndex={activeTabIndex}
          index={index}
          tabs={item}
          key={index}
          theme={theme}
        />
      ))}
    </div>
  );
};

const IframeTab = ({ options, index, tabs, theme, activeTabIndex }) => {
  const styles = themedStyle(theme);
  const iframeRef = useRef();
  const [url, setUrl] = useState(tabs.url);
  const [srcurl, setSrcUrl] = useState(tabs.url);
  const [stack, setStack] = useState([]);
  const [counter, setCounter] = useState(0);
  const localStack =
    JSON.parse(window.localStorage.getItem('browser-tabs')) ?? [];

  useEffect(() => {
    const commonStack = [tabs.url];
    if (localStack && options?.continueLeftOff) {
      const parsed = localStack[index].stack ?? [];
      setStack(parsed);
      setSrcUrl(parsed[parsed.length - 1]);
    } else {
      setStack(commonStack);
      localStack[index].stack = commonStack;
      window.localStorage.setItem('browser-tabs', JSON.stringify(localStack));
    }
  }, []);

  return (
    <div
      key={index}
      style={{
        ...styles.content,
        flex: 1,
        display: index != activeTabIndex && 'none'
      }}>
      {options?.allowSearchBar && (
        <div style={styles.inputContainer}>
          <div
            style={styles.icon}
            onClick={() => {
              if (counter != 0) {
                setSrcUrl(stack[counter - 1]);
                setCounter(counter - 1);
              }
            }}>
            <LeftArrowIcon />
          </div>
          <div
            style={styles.icon}
            onClick={() => {
              if (counter < stack.length) {
                setSrcUrl(stack[counter + 1]);
                setCounter(counter + 1);
              }
            }}>
            <RightArrowIcon />
          </div>
          <div
            style={styles.icon}
            onClick={() => (iframeRef.current.src = srcurl)}>
            <RefreshIcon />
          </div>
          <TextField
            fullWidth
            sx={styles.inputStyle}
            InputProps={{
              style: styles.inputStyle
            }}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter') {
                ev.preventDefault();
                const search =
                  'https://www.google.com/search?q=' +
                  join(ev.target.value.split(' '), '+') +
                  '&igu=1';
                setSrcUrl(search);
                stack.push(search);
                setStack(stack);
                localStack[index].stack = stack;
                window.localStorage.setItem(
                  'browser-tabs',
                  JSON.stringify(localStack)
                );
              }
            }}
            variant="outlined"
            size="small"
            placeholder="search here"
            id="margin-none"
          />
        </div>
      )}
      <iframe
        ref={iframeRef}
        title={tabs.title}
        src={srcurl}
        name="vwbrowser"
        width="100%"
        height="100%"
        target="_parent"
        frameBorder="no"
        sandbox="allow-modals allow-forms allow-popups allow-scripts allow-same-origin"
        style={{ backgroundColor: 'white' }}
        onLoad={() => {
          // Handle iframe load completion and set tab.isLoading accordingly.
        }}
        className="browser-iframe"
      />
    </div>
  );
};
const themedStyle = (theme) => ({
  tabs: {
    display: 'flex',
    position: 'relative',
    justifyContent: 'space-between',
    backgroundColor: theme.topBarColor
  },
  tab: {
    minWidth: '150px',
    maxWidth: '280px',
    overflow: 'hidden',
    display: 'flex'
  },
  activeTab: { zIndex: '40', position: 'relative', paddingBottom: '1px' },
  tabBox: {
    flex: '1',
    minWidth: '100px',
    borderRadius: '4px',
    display: 'flex',
    backgroundColor: theme.tabColor
  },
  activeTabBox: {
    flex: 1,
    display: 'flex',
    marginLeft: 4,
    marginTop: 4,
    width: '100%',
    height: '100%',
    backgroundColor: theme.activeTabColor,
    borderRadius: 5
  },
  content: {
    background: theme.contentColor,
    position: 'relative',
    height: '100%'
  },
  border: {
    margin: '8px 0',
    height: 19,
    borderRight: '1px solid rgb(0 0 0 / 6%)'
  },
  title: {
    color: theme.labelColor,
    fontSize: 12,
    flex: 1,
    marginLeft: 5,
    lineHeight: '15px',
    overflow: 'hidden'
  },
  cross: {
    flexGrow: '0',
    flexShrink: '0',
    position: 'relative',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '8px 8px'
  },
  inputContainer: {
    display: 'flex',
    backgroundColor: 'white',
    padding: 5,
    width: '80%',
    zIndex: 0
  },
  icon: {
    padding: 5
  },
  inputStyle: {
    borderRadius: 100,
    width: '100%',
    borderWidth: 1,
    borderColor: 'grey',
    color: 'black',
    fontFamily: 'SF UI Display, sans-serif',
    fontStyle: 'normal',
    fontWeight: 300,
    fontSize: '12px',
    textTransform: 'lowercase',
    '& label.MuiFocused': {
      color: '#A0AAB4'
    },
    '& .MuiInputUnderline:after': {
      borderBottom: 0
    },
    '& .MuiOutlinedInputRoot': {
      '& fieldset': {
        borderColor: '#E0E3E7'
      },
      '&:hover fieldset': {
        borderColor: '#E3E6E9'
      },
      '&.MuiFocused fieldset': {
        borderColor: '#A8C3F8',
        borderWidth: 1
      }
    },
    '& .MuiOutlinedInputInput': {
      borderRadius: 50,
      position: 'relative',
      backgroundColor: '#F3F6F9',
      border: '1px solid',
      borderColor: '#E0E3E7',
      fontSize: 12,
      padding: '5px 10px',
      fontFamily: 'SF UI Display, sans-serif',
      '&.focus': {
        boxShadow: `rgba(168, 195, 248, 0.25) 0 0 0 0.2rem`,
        borderColor: '#A8C3F8'
      },
      '&:hover': {
        backgroundColor: '#E3E6E9',
        borderColor: '#E3E6E9'
      }
    }
  },
  leaveButton: {
    backgroundColor: '#FF5050',
    borderRadius: '12px',
    color: 'white',
    padding: '12px 10px',
    margin: 5,
    fontFamily: 'SF UI Display, sans-serif',
    fontStyle: 'normal',
    fontWeight: 300,
    fontSize: '16px',
    textTransform: 'lowercase',
    position: 'absolute',
    right: 0,
    zIndex: 2
  }
});

function tabsLoop(
  options,
  currTabs,
  activeTabIndex,
  tabStyle,
  activeTabsStyle,
  tabBoxStyle,
  activeTabBoxStyle,
  setActiveTabIndex,
  setNewTabs,
  onAddTabPress,
  theme
) {
  return currTabs.map((tabs, index) => {
    const styles = themedStyle(theme);
    const isLast = index == currTabs.length - 1;
    const lastStyle = isLast && { maxWidth: 280 + 45 };
    const active =
      activeTabIndex == index
        ? {
            ...styles.tab,
            ...styles.activeTab,
            ...lastStyle,
            ...tabStyle,
            ...activeTabsStyle
          }
        : { ...styles.tab, ...lastStyle, ...tabStyle };
    const activeTabBox =
      activeTabIndex == index
        ? {
            ...styles.tabbox,
            ...styles.activeTabBox,
            ...tabBoxStyle,
            ...activeTabBoxStyle
          }
        : { ...styles.tabBox, ...tabBoxStyle };

    return (
      <div key={'tabs' + index} style={active}>
        <Tab
          activeTabBox={activeTabBox}
          setActiveTabIndex={setActiveTabIndex}
          index={index}
          tabs={tabs}
          theme={theme}
          currTabs={currTabs}
          activeTabIndex={activeTabIndex}
          setNewTabs={setNewTabs}
        />
        {activeTabIndex - 1 != index && activeTabIndex != index && !isLast && (
          <div style={styles.border}></div>
        )}
        {isLast && options?.allowNewTabs && (
          <AddButton theme={theme} onAddTabPress={onAddTabPress} />
        )}
      </div>
    );
  });
}

function AddButton({ theme, onAddTabPress }) {
  const [hover, sethover] = useState(false);
  return (
    <div
      onMouseEnter={() => sethover(true)}
      onMouseLeave={() => sethover(false)}
      onClick={onAddTabPress}
      style={{
        marginTop: 5,
        marginLeft: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: hover ? theme.addButtonHoverColor : theme.addButtonColor
      }}>
      <AddIcon width={20} height={20} fill={theme.addButton} />
    </div>
  );
}
AddButton.propTypes = {
  setActiveTabIndex: PropTypes.func,
  setNewTabs: PropTypes.func,
  currTabs: PropTypes.array,
  index: PropTypes.number,
  theme: PropTypes.object,
  onAddTabPress: PropTypes.func
};
function Tab({
  activeTabBox,
  setActiveTabIndex,
  index,
  tabs,
  theme,
  activeTabIndex,
  setNewTabs,
  currTabs
}) {
  const styles = themedStyle(theme);
  const [hover, sethover] = useState(false);

  const hovered = () =>
    activeTabIndex != index &&
    hover && {
      backgroundColor: theme.tabHoverColor,
      borderRadius: 4
    };
  return (
    <div
      onMouseEnter={() => sethover(true)}
      onMouseLeave={() => sethover(false)}
      style={{ ...activeTabBox, borderRadius: 5, ...hovered() }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%'
        }}>
        <div
          onClick={() => setActiveTabIndex(index)}
          style={{
            cursor: 'pointer',
            display: 'flex',
            flex: 1,
            height: '100%',
            padding: 3,
            alignItems: 'center'
          }}>
          <img src={'https://www.google.com/s2/favicons?domain=' + tabs.url} />
          <span style={styles.title}>{tabs.title}</span>
        </div>
        <div
          onClick={() => {
            if (currTabs.length == 1) return;
            const tabs = currTabs.filter((e, i) => i != index);
            setActiveTabIndex(activeTabIndex > 0 ? activeTabIndex - 1 : 0);
            setNewTabs(tabs);
          }}
          style={{
            width: 8,
            display: 'flex',
            height: 8,
            marginRight: 10,
            alignSelf: 'center'
          }}>
          <CrossIcon />
        </div>
      </div>
    </div>
  );
}
export default React.memo(withBoundary(Browser));
IframeTab.propTypes = {
  options: PropTypes.object,
  tabs: PropTypes.object,
  index: PropTypes.number,
  theme: PropTypes.object,
  activeTabIndex: PropTypes.number
};
Tab.propTypes = {
  activeTabBox: PropTypes.object,
  setActiveTabIndex: PropTypes.func,
  index: PropTypes.number,
  tabs: PropTypes.object,
  theme: PropTypes.object,
  activeTabIndex: PropTypes.number,
  setNewTabs: PropTypes.func,
  currTabs: PropTypes.array
};
Browser.propTypes = {
  options: PropTypes.object,
  style: PropTypes.object,
  theme: PropTypes.object,
  onAddTabPress: PropTypes.func,
  onLeave: PropTypes.func,
  injectProps: PropTypes.object,
  tabs: PropTypes.array,
  activeTab: PropTypes.number
};
export const Dark = {
  contentColor: '#252729',
  activeTabColor: '#323639',
  topBarColor: '#202124',
  tabColor: 'transparent',
  tabHoverColor: '#292b2e',
  cancelButtonColor: '#71757a',
  cancelButtonHoverColor: 'red',
  cancelActiveButtonColor: '#858b8f',
  cancelActiveButtonHoverColor: 'red',
  labelColor: '#9ca1a7',
  addButtonColor: 'transparent',
  addButtonHoverColor: '#292b2e',
  addButton: '#71757a'
};
export const Light = {
  contentColor: 'white',
  activeTabColor: 'white',
  topBarColor: '#dee1e6',
  tabColor: 'transparent',
  tabHoverColor: '#f4f5f6',
  cancelButtonColor: 'black',
  cancelButtonHoverColor: 'red',
  cancelActiveButtonColor: 'black',
  cancelActiveButtonHoverColor: 'red',
  labelColor: '#45474a',
  addButtonColor: 'transparent',
  addButtonHoverColor: '#ffffff26',
  addButton: '#45474a'
};
